/**
 * useGameSocket - React hook for Fog Chess Socket.IO connection
 * Enforces strict listener cleanup in useEffect teardown to prevent
 * event duplication under React 18 StrictMode
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import type {
  ClientToServerEvents,
  ServerToClientEvents,
  SocketAuth,
  SocketConfig,
  ConnectionState,
  FogViewPayload,
  VisibleMovePayload,
  CaptureEventPayload,
  EndgameResultPayload,
  MatchStartedPayload,
  RoomStatePayload,
  OpponentDisconnectedPayload,
  OpponentReconnectedPayload,
  CheckWarningPayload,
  QueueJoinedPayload,
  QueueLeftPayload,
} from '../../types/socket';
import { DEFAULT_SOCKET_CONFIG } from '../../types/socket';

/**
 * Socket connection state
 */
export interface UseGameSocketState {
  connectionState: ConnectionState;
  socket: Socket<ServerToClientEvents, ClientToServerEvents> | null;
  isConnected: boolean;
  reconnectAttemptCount: number;
  lastError: Error | null;
}

/**
 * Socket event handlers
 * Matches ServerToClientEvents from types/socket.ts
 */
export interface UseGameSocketHandlers {
  onMatchJoined?: (payload: { match_id: string; color: 'white' | 'black' }) => void;
  onMatchStarted?: (payload: MatchStartedPayload) => void;
  onGameState?: (payload: FogViewPayload) => void;
  onMoveApplied?: (payload: VisibleMovePayload) => void;
  onPieceCaptured?: (payload: CaptureEventPayload) => void;
  onCheckWarning?: (payload: CheckWarningPayload) => void;
  onGameOver?: (payload: EndgameResultPayload) => void;
  onQueueJoined?: (payload: QueueJoinedPayload) => void;
  onQueueLeft?: (payload: QueueLeftPayload) => void;
  onRoomCreated?: (payload: { room_code: string; room_id: string; state: RoomStatePayload }) => void;
  onRoomState?: (payload: RoomStatePayload) => void;
  onRoomLeft?: (payload: { room_code: string; state?: RoomStatePayload }) => void;
  onRoomKicked?: (payload: { room_code: string }) => void;
  onRoomStarted?: (payload: { room_code: string; match_id: string }) => void;
  onOpponentDisconnected?: (payload: OpponentDisconnectedPayload) => void;
  onOpponentReconnected?: (payload: OpponentReconnectedPayload) => void;
  onServerRebooting?: (payload: { grace_seconds: number; message: string }) => void;
  onError?: (payload: { reason: string; details?: Record<string, unknown>; code?: string }) => void;
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onConnectError?: (error: Error) => void;
}

/**
 * Hook return type
 */
export interface UseGameSocketReturn extends UseGameSocketState {
  connect: (auth: SocketAuth) => void;
  disconnect: () => void;
  emit: <K extends keyof ClientToServerEvents>(
    event: K,
    ...args: Parameters<ClientToServerEvents[K]>
  ) => void;
  on: <K extends keyof ServerToClientEvents>(
    event: K,
    handler: ServerToClientEvents[K]
  ) => void;
  off: <K extends keyof ServerToClientEvents>(
    event: K,
    handler?: ServerToClientEvents[K]
  ) => void;
  // Convenience methods
  joinMatch: (playerId: string, matchId: string) => void;
  attemptReconnect: (matchId: string) => void;
  joinQueue: () => void;
  leaveQueue: () => void;
  submitSetup: (color: 'white' | 'black', placements: Array<{ square: string; type: string }>) => void;
  submitMove: (from: string, to: string, promotion?: 'q' | 'r' | 'b' | 'n') => void;
  resign: () => void;
  createRoom: (mode: 'match' | 'tournament') => void;
  joinRoom: (roomCode: string) => void;
  leaveRoom: (roomCode?: string) => void;
  pickSeat: (roomCode: string, seatIndex: number) => void;
  kickMember: (roomCode: string, targetUserId: string) => void;
  startRoom: (roomCode: string) => void;
  requestRoomState: (roomCode: string) => void;
  joinSpectate: (matchId: string, roomCode: string) => void;
  leaveSpectate: () => void;
  tournamentReady: (tournamentId: string, matchId: string) => void;
}

/**
 * Create socket hook with custom config
 */
export function createUseGameSocket(config?: Partial<SocketConfig>) {
  const mergedConfig = { ...DEFAULT_SOCKET_CONFIG, ...config };
  
  return function useGameSocket(handlers: UseGameSocketHandlers = {}): UseGameSocketReturn {
    const socketRef = useRef<Socket<ServerToClientEvents, ClientToServerEvents> | null>(null);
    const handlersRef = useRef(handlers);
    const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
    const [reconnectAttemptCount, setReconnectAttemptCount] = useState(0);
    const [lastError, setLastError] = useState<Error | null>(null);
    
    // Keep handlers ref updated
    useEffect(() => {
      handlersRef.current = handlers;
    }, [handlers]);
    
    // Safe emit wrapper
    const emit = useCallback(<K extends keyof ClientToServerEvents>(
      event: K,
      ...args: Parameters<ClientToServerEvents[K]>
    ) => {
      (socketRef.current as any)?.emit(event, ...args);
    }, []);
    
    // Safe on wrapper with automatic cleanup tracking
    const on = useCallback(<K extends keyof ServerToClientEvents>(
      event: K,
      handler: ServerToClientEvents[K]
    ) => {
      (socketRef.current as any)?.on(event, handler);
    }, []);
    
    // Safe off wrapper
    const off = useCallback(<K extends keyof ServerToClientEvents>(
      event: K,
      handler?: ServerToClientEvents[K]
    ) => {
      (socketRef.current as any)?.off(event, handler);
    }, []);
    
    // Convenience methods
    const joinMatch = useCallback((playerId: string, matchId: string) => {
      emit('match:join', { player_id: playerId, match_id: matchId });
    }, [emit]);
    
    const attemptReconnect = useCallback((matchId: string) => {
      emit('reconnect:attempt', { match_id: matchId });
    }, [emit]);
    
    const joinQueue = useCallback(() => {
      emit('queue:join', {});
    }, [emit]);
    
    const leaveQueue = useCallback(() => {
      emit('queue:leave', {});
    }, [emit]);
    
    const submitSetup = useCallback((
      color: 'white' | 'black',
      placements: Array<{ square: string; type: string }>
    ) => {
      emit('setup:submit', { color, placements } as any);
    }, [emit]);
    
    const submitMove = useCallback((
      from: string,
      to: string,
      promotion?: 'q' | 'r' | 'b' | 'n'
    ) => {
      emit('move:submit', { from, to, promotion } as any);
    }, [emit]);
    
    const resign = useCallback(() => {
      emit('resign', {});
    }, [emit]);
    
    const createRoom = useCallback((mode: 'match' | 'tournament') => {
      emit('room:create', { mode });
    }, [emit]);
    
    const joinRoom = useCallback((roomCode: string) => {
      emit('room:join', { room_code: roomCode });
    }, [emit]);
    
    const leaveRoom = useCallback((roomCode?: string) => {
      emit('room:leave', { room_code: roomCode ?? '' } as any);
    }, [emit]);
    
    const pickSeat = useCallback((roomCode: string, seatIndex: number) => {
      emit('room:pick_seat', { room_code: roomCode, seat_index: seatIndex } as any);
    }, [emit]);
    
    const kickMember = useCallback((roomCode: string, targetUserId: string) => {
      emit('room:kick', { room_code: roomCode, target_user_id: targetUserId });
    }, [emit]);
    
    const startRoom = useCallback((roomCode: string) => {
      emit('room:start', { room_code: roomCode });
    }, [emit]);
    
    const requestRoomState = useCallback((roomCode: string) => {
      emit('room:state', { room_code: roomCode });
    }, [emit]);
    
    const joinSpectate = useCallback((matchId: string, roomCode: string) => {
      emit('spectate:join', { match_id: matchId, room_code: roomCode });
    }, [emit]);
    
    const leaveSpectate = useCallback(() => {
      emit('spectate:leave', {});
    }, [emit]);
    
    const tournamentReady = useCallback((tournamentId: string, matchId: string) => {
      emit('tournament:ready', { tournament_id: tournamentId, match_id: matchId });
    }, [emit]);
    
    // Main connection effect with STRICT cleanup
    useEffect(() => {
      // Create socket instance
      const socket = io(mergedConfig.url, {
        auth: { token: '' }, // Will be set on connect
        reconnection: mergedConfig.reconnection,
        reconnectionAttempts: mergedConfig.reconnectionAttempts,
        reconnectionDelay: mergedConfig.reconnectionDelay,
        reconnectionDelayMax: mergedConfig.reconnectionDelayMax,
        timeout: mergedConfig.timeout,
        transports: mergedConfig.transports,
      }) as any; // Use 'any' to bypass strict socket.io-client generics
      
      socketRef.current = socket;
      
      // Connection event handlers
      const onConnect = () => {
        setConnectionState('connected');
        setReconnectAttemptCount(0);
        setLastError(null);
        handlersRef.current.onConnect?.();
      };
      
      const onDisconnect = (reason: string) => {
        setConnectionState('disconnected');
        handlersRef.current.onDisconnect?.(reason);
      };
      
      const onConnectError = (error: Error) => {
        setConnectionState('error');
        setLastError(error);
        handlersRef.current.onConnectError?.(error);
      };
      
      // Register core socket lifecycle listeners
      socket.on('connect', onConnect);
      socket.on('disconnect', onDisconnect);
      socket.on('connect_error', onConnectError);
      
      // Register game event handlers from props
      const h = handlersRef.current;
      
      if (h.onMatchJoined) socket.on('match:joined', h.onMatchJoined as any);
      if (h.onMatchStarted) socket.on('match:started', h.onMatchStarted as any);
      if (h.onGameState) socket.on('game:state', h.onGameState as any);
      if (h.onMoveApplied) socket.on('move:applied', h.onMoveApplied as any);
      if (h.onPieceCaptured) socket.on('piece:captured', h.onPieceCaptured as any);
      if (h.onCheckWarning) socket.on('check:warning', h.onCheckWarning as any);
      if (h.onGameOver) socket.on('game:over', h.onGameOver as any);
      if (h.onQueueJoined) socket.on('queue:joined', h.onQueueJoined as any);
      if (h.onQueueLeft) socket.on('queue:left', h.onQueueLeft as any);
      if (h.onRoomCreated) socket.on('room:created', h.onRoomCreated as any);
      if (h.onRoomState) socket.on('room:state', h.onRoomState as any);
      if (h.onRoomLeft) socket.on('room:left', h.onRoomLeft as any);
      if (h.onRoomKicked) socket.on('room:kicked', h.onRoomKicked as any);
      if (h.onRoomStarted) socket.on('room:started', h.onRoomStarted as any);
      if (h.onOpponentDisconnected) socket.on('opponent:disconnected', h.onOpponentDisconnected as any);
      if (h.onOpponentReconnected) socket.on('opponent:reconnected', h.onOpponentReconnected as any);
      if (h.onServerRebooting) socket.on('server:rebooting', h.onServerRebooting as any);
      if (h.onError) {
        // Register for all error events
        const errorEvents = [
          'error:server_rebooting',
          'error:invalid_payload',
          'error:unknown_match',
          'error:not_in_match',
          'error:not_authenticated',
          'error:wrong_phase',
          'error:feature_disabled',
          'error:service_unavailable',
          'error:gate_locked',
          'error:already_queued',
          'error:not_joined',
          'error:no_active_match',
          'error:lock_busy',
          'error:corrupted_state',
          'error:not_your_turn',
          'error:internal',
          'error:not_found',
          'error:not_room_owner',
          'error:room_not_ready',
          'error:not_room_member',
          'error:cannot_spectate_own_match',
          'error:spectate_not_available',
        ] as const;
        
        errorEvents.forEach((event) => {
          socket.on(event, h.onError! as any);
        });
      }
      
      // Return cleanup function - CRITICAL for React 18 StrictMode
      return () => {
        // Remove ALL listeners - this prevents duplication on re-mount
        socket.off('connect', onConnect);
        socket.off('disconnect', onDisconnect);
        socket.off('connect_error', onConnectError);
        
        // Remove all game event listeners
        if (h.onMatchJoined) socket.off('match:joined', h.onMatchJoined as any);
        if (h.onMatchStarted) socket.off('match:started', h.onMatchStarted as any);
        if (h.onGameState) socket.off('game:state', h.onGameState as any);
        if (h.onMoveApplied) socket.off('move:applied', h.onMoveApplied as any);
        if (h.onPieceCaptured) socket.off('piece:captured', h.onPieceCaptured as any);
        if (h.onCheckWarning) socket.off('check:warning', h.onCheckWarning as any);
        if (h.onGameOver) socket.off('game:over', h.onGameOver as any);
        if (h.onQueueJoined) socket.off('queue:joined', h.onQueueJoined as any);
        if (h.onQueueLeft) socket.off('queue:left', h.onQueueLeft as any);
        if (h.onRoomCreated) socket.off('room:created', h.onRoomCreated as any);
        if (h.onRoomState) socket.off('room:state', h.onRoomState as any);
        if (h.onRoomLeft) socket.off('room:left', h.onRoomLeft as any);
        if (h.onRoomKicked) socket.off('room:kicked', h.onRoomKicked as any);
        if (h.onRoomStarted) socket.off('room:started', h.onRoomStarted as any);
        if (h.onOpponentDisconnected) socket.off('opponent:disconnected', h.onOpponentDisconnected as any);
        if (h.onOpponentReconnected) socket.off('opponent:reconnected', h.onOpponentReconnected as any);
        if (h.onServerRebooting) socket.off('server:rebooting', h.onServerRebooting as any);
        if (h.onError) {
          const errorEvents = [
            'error:server_rebooting',
            'error:invalid_payload',
            'error:unknown_match',
            'error:not_in_match',
            'error:not_authenticated',
            'error:wrong_phase',
            'error:feature_disabled',
            'error:service_unavailable',
            'error:gate_locked',
            'error:already_queued',
            'error:not_joined',
            'error:no_active_match',
            'error:lock_busy',
            'error:corrupted_state',
            'error:not_your_turn',
            'error:internal',
            'error:not_found',
            'error:not_room_owner',
            'error:room_not_ready',
            'error:not_room_member',
            'error:cannot_spectate_own_match',
            'error:spectate_not_available',
          ] as const;
          
          errorEvents.forEach((event) => {
            socket.off(event, h.onError!);
          });
        }
        
        // Force disconnect and cleanup
        socket.disconnect();
        socketRef.current = null;
        setConnectionState('disconnected');
      };
    }, [mergedConfig.url]); // Only recreate if URL changes
    
    // Connect function - sets auth and connects
    const connect = useCallback((auth: SocketAuth) => {
      const socket = socketRef.current;
      if (!socket) return;
      
      if (socket.connected) return;
      
      setConnectionState('connecting');
      socket.auth = auth;
      socket.connect();
    }, []);
    
    // Disconnect function
    const disconnect = useCallback(() => {
      const socket = socketRef.current;
      if (!socket) return;
      
      socket.disconnect();
    }, []);
    
    return {
      connectionState,
      socket: socketRef.current,
      isConnected: connectionState === 'connected',
      reconnectAttemptCount,
      lastError,
      connect,
      disconnect,
      emit,
      on,
      off,
      joinMatch,
      attemptReconnect,
      joinQueue,
      leaveQueue,
      submitSetup,
      submitMove,
      resign,
      createRoom,
      joinRoom,
      leaveRoom,
      pickSeat,
      kickMember,
      startRoom,
      requestRoomState,
      joinSpectate,
      leaveSpectate,
      tournamentReady,
    };
  };
}

/**
 * Default hook instance
 */
export const useGameSocket = createUseGameSocket();